import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNav from '@/components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOffers = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_claims: 0, eligible_count: 0 });

  const fetchOffers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const response = await axios.get(`${API}/offers/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setOffers(response.data.offers);
        setStats({
          total_claims: response.data.total_claims,
          eligible_count: response.data.eligible_count
        });
      }
    } catch (error) {
      console.error('Fetch offers error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load offers');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/offers/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `offers_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Offers exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export offers');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading offers...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb', 
      padding: '40px 20px' 
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              ← Back to Home
            </button>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px', color: '#1f2937' }}>
              🎁 Launch Offer Dashboard
            </h1>
            <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
              Track first 50 free yearly subscriptions
            </p>
          </div>

          <button
            onClick={handleExport}
            style={{
              padding: '12px 24px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
            }}
          >
            📥 Export to CSV
          </button>
        </div>

        {/* Admin Navigation */}
        <AdminNav />

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Total Claims</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2563eb' }}>
              {stats.total_claims}
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Eligible (≤50)</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981' }}>
              {stats.eligible_count}
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Exceeded</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#ef4444' }}>
              {stats.total_claims - stats.eligible_count}
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Remaining Slots</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#f59e0b' }}>
              {Math.max(0, 50 - stats.eligible_count)}
            </p>
          </div>
        </div>

        {/* Offers Table */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>#</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Country</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Claimed At</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>IP Address</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {offers.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      No offers claimed yet
                    </td>
                  </tr>
                ) : (
                  offers.map((offer) => (
                    <tr key={offer.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '16px', fontWeight: '600', color: offer.eligible ? '#10b981' : '#ef4444' }}>
                        {offer.claimed_number}
                      </td>
                      <td style={{ padding: '16px', color: '#1f2937' }}>{offer.email}</td>
                      <td style={{ padding: '16px', color: '#6b7280' }}>{offer.country || '-'}</td>
                      <td style={{ padding: '16px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {new Date(offer.claimed_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {offer.ip_address}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: offer.eligible ? '#d1fae5' : '#fee2e2',
                          color: offer.eligible ? '#065f46' : '#991b1b'
                        }}>
                          {offer.status === 'activated' ? '✅ Activated' : 
                           offer.eligible ? '✓ Eligible' : '✗ Exceeded'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOffers;
